import React from 'react';
import {Box, Tooltip} from '@mui/material';
import DistrictChildRelationIcon from './DistrictChildRelationIcon';

interface SharedMessageRowBadgesProps {
  ownerCompanyName: string;
}

/**
 * Inline marker shown in message list rows for child partners.
 *
 * Sits immediately left of the message name so the district→child
 * relationship is obvious at a glance. The icon is rendered in the
 * theme's primary color and at a fontSize chosen to stay legible
 * next to the row's body2 text — far more visible than the previous
 * monochrome 18px asset image.
 *
 * Tooltip names the originating district and reminds the user the
 * row is read-only on a partner account.
 */
const SharedMessageRowBadges: React.FC<SharedMessageRowBadgesProps> = ({
  ownerCompanyName,
}) => {
  const districtLabel = ownerCompanyName?.trim() || 'the district';
  const tooltipLabel = `Shared by ${districtLabel} (District) · Read-only on your partner account`;

  return (
    <Tooltip title={tooltipLabel} arrow>
      <Box
        component='span'
        role='img'
        tabIndex={0}
        aria-label={tooltipLabel}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'help',
          color: 'primary.main',
          outline: 'none',
          borderRadius: '4px',
          flexShrink: 0,
          lineHeight: 0,
          '&:hover': {
            color: 'primary.dark',
          },
          '&:focus-visible': {
            boxShadow: theme => `0 0 0 2px ${theme.palette.primary.main}`,
          },
        }}>
        <DistrictChildRelationIcon
          aria-hidden='true'
          sx={{
            fontSize: 22,
            // A matching stroke thickens the line-art so the icon reads
            // bold at small sizes without growing the bounding box.
            stroke: 'currentColor',
            strokeWidth: 6,
          }}
        />
      </Box>
    </Tooltip>
  );
};

export default SharedMessageRowBadges;
